import React from "react";
import { Box, Container, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import Seo from "../components/Seo";
import StatusDot from "../components/StatusDot";
import { useSiteStatus } from "../hooks/useSiteStatus";
import { describeChecked } from "../utils/lastChecked";

const cadenceText = (seconds) => (seconds < 120 ? "about every minute" : `every ${Math.round(seconds / 60)} minutes`);
// Best Buy is polled every minute, but a given product comes round about every four.

/**
 * Public polling status (#283): the same last-checked fields the daily health
 * email reads, so this page can never disagree with the report.
 */
export const StatusPage = () => {
    const { status, retailers, error, now, summary } = useSiteStatus();

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Seo
                title="System Status | StockMaid"
                description="How many products StockMaid is tracking at Best Buy, Micro Center and Newegg, how often each is checked, and when each was last checked."
                canonicalPath="/status"
            />
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                    System status
                </Typography>

                {error && !status && (
                    <Typography color="error">Status is unavailable right now.</Typography>
                )}

                {status && summary && (
                    <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                            <StatusDot level={summary.level} size={10} />
                            <Typography fontWeight={600}>
                                {summary.label} · {summary.tracked.toLocaleString()} listings tracked
                            </Typography>
                        </Box>

                        <Box sx={{ overflowX: "auto" }}>
                            <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Retailer</TableCell>
                                        <TableCell align="right">Tracked</TableCell>
                                        <TableCell>Checked</TableCell>
                                        <TableCell>Most recent check</TableCell>
                                        <TableCell>Oldest check</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {retailers.map((r) => {
                                        const newest = describeChecked(r.newestCheck, r.pollSeconds, { now });
                                        const oldest = describeChecked(r.oldestCheck, r.pollSeconds, { now });
                                        return (
                                            <TableRow key={r.key}>
                                                <TableCell sx={{ fontWeight: 600 }}>{r.label}</TableCell>
                                                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{r.tracked.toLocaleString()}</TableCell>
                                                <TableCell>{cadenceText(r.pollSeconds)}</TableCell>
                                                <TableCell sx={{ color: newest.stale ? "text.secondary" : "#21b6b7", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                                    {newest.text || "Not yet"}
                                                </TableCell>
                                                <TableCell sx={{ color: oldest.stale ? "warning.main" : "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                                                    {oldest.text || "Not yet"}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default StatusPage;
